import ProductDataService from '@modules/order/services/ProductDataService'
import getActivityHierarchyMap from '@modules/order/utils/get-activity-hierarchy-map'
import { CancellableRequest } from '@services/HttpService'
import {
  HIERARCHY_API_KEY,
  PaginatedHierarchyItemsDataStore
} from '@stores/paginated-hierarchy-items/PaginatedHierarchyItemsDataStore'
import join from 'lodash/join'
import map from 'lodash/map'
import { action, computed, observable, reaction, toJS } from 'mobx'
import { nav, stores } from '@stores'
import { OrderType } from '@modules/common/models/enums'
import {
  CATEGORY_UI_SEPERATOR,
  CLIENT_KPI_ORDER_LEVEL_AGGR,
  CLIENT_ORDER_LEVEL_GROUP_ATTR,
  DOOR_KPI_ORDER_LEVEL_AGGR,
  DOOR_ORDER_LEVEL_GROUP_ATTR,
  KPI_TOPBAR_METRIC_ATTRIBUTES,
  METRIC_ATTRIBUTES,
  PRODUCT_REMOVE_LOADER_KEY
} from '@modules/order/constants/uiConstants'
import { orderKPIGlobalTopBarStore } from '../../OrderKPIGlobalTopBarStore'
import { getChannelName } from '@utils/get-channel-name'
import { SORT_ORDERS } from '@components/UI-Components/EnhancedView/Sort/sort_constants'
import {
  addHierarchyNamesToProductsData,
  formatProductGroupsData,
  getCurrentGroupMapping,
  mapNewFiltersToIds,
  parseAppliedSort,
  removeSkippedGroups
} from '@modules/order/utils/dynamic-group-mapping'
import cloneDeep from 'lodash/cloneDeep'
import {
  MANDATORY_ORDER_ATTRIBUTE_FIELDS,
  PRODUCT_SERVICE_ENDPOINTS
} from '@modules/showroom/productCollection/enhancedView/constants/cnf-product-constants'
import uniq from 'lodash/uniq'
import { viewToConfigMapping } from '@modules/order/constants/views'
import { config } from '@config'
import { isEmptyValue } from '@utils/object-utils'
import { OrderService } from '@modules/order/services/orderService'
import { getEnumValue } from '@utils/get-enum-value'
import { VIPClientType } from '@modules/common/models/enums/VIPClientType'
import filter from 'lodash/filter'

export class ProductDataStore extends PaginatedHierarchyItemsDataStore {
  private orderService = new OrderService()
  private overviewReactions = []
  private removedProductsResCache: any = {}
  @observable.shallow hierarchyWithoutFilters = []
  @observable currentViewName = 'orderCollection'

  @action
  setCurrentViewName = viewName => {
    this.currentViewName = viewToConfigMapping[viewName]
  }

  @computed get isEnhancedViewEnabled() {
    const {
      productConfiguration: { enhancedViewEnabled }
    } = stores
    return enhancedViewEnabled
  }

  private service = new ProductDataService()
  fetchItemsFromAPI(
    fromIndex: number,
    toIndex: number
  ): CancellableRequest<any> | Promise<any[]> {
    const {
      nav: {
        queryParams: {
          buyingSessionGroupId,
          orderId,
          orderType,
          clientOrderId,
          clientType,
          clientId
        }
      }
    } = stores

    if (!orderId || !buyingSessionGroupId) return undefined

    if (this.isEnhancedViewEnabled) {
      return this.fetchItemsFromNewAPI({
        fromIndex,
        toIndex,
        buyingSessionGroupId,
        orderId,
        orderType,
        clientOrderId,
        clientType,
        clientId
      })
    }
    return this.fetchItemsFromOldAPI({
      fromIndex,
      toIndex,
      buyingSessionGroupId,
      orderId,
      orderType
    })
  }

  fetchItemsFromOldAPI = ({
    fromIndex,
    toIndex,
    buyingSessionGroupId,
    orderId,
    orderType
  }) => {
    const { activity, family, line } = getActivityHierarchyMap(
      Array.from(this.hierarchyInRange(fromIndex, toIndex, this.heirarchy, 0))
    )
    return this.service.fetchProductsByHierarchy(
      buyingSessionGroupId,
      orderId,
      orderType,
      {
        ...this.mapFiltersToIds(this.filterStore.appliedFilters),
        activityName: [activity.name],
        ...(family ? { familyName: [family.name] } : {}),
        ...(line ? { lineName: [line.name] } : {})
      },
      this.searchKey,
      true
    )
  }

  fetchItemsFromNewAPI = (params: any) => {
    const {
      fromIndex,
      toIndex,
      buyingSessionGroupId,
      orderId,
      orderType,
      clientOrderId,
      clientType
    } = params
    const {
      productConfiguration: {
        getActionAttributes,
        getViewProductCardAttributes,
        defaultValuesFromConfig,
        findSpecificAttribute
      }
    } = stores
    let mapping = getCurrentGroupMapping(
      this.hierarchyInRange(fromIndex, toIndex, this.heirarchy, 0),
      defaultValuesFromConfig?.empty,
      findSpecificAttribute
    )
    const channelName = getChannelName(clientType)
    const attrConfig = getViewProductCardAttributes(channelName, this.currentViewName)
    const searchableAttributes = getActionAttributes(
      'searchBy',
      channelName,
      this.currentViewName,
      true
    )
    const body = {
      sort: parseAppliedSort(this.enhancedSortStore.selectedSortOption),
      filters: {
        ...(mapping ? mapping : {}),
        ...mapNewFiltersToIds(this.cnfProductFilterStore.selected)
      },
      excludeFilters: {
        ...mapNewFiltersToIds(this.cnfProductFilterStore.excludedFilters || {})
      },
      ...(isEmptyValue(this.searchKey)
        ? {}
        : {
            search: this.searchKey,
            searchAttributes: this.searchKey ? searchableAttributes : []
          }),
      attributes: uniq(
        MANDATORY_ORDER_ATTRIBUTE_FIELDS.concat(attrConfig.map(item => item.attributeId))
      )
    }
    return this.service
      .fetchProductsByGrouping(
        buyingSessionGroupId,
        orderId,
        orderType,
        clientOrderId,
        body
      )
      .then(resp => {
        return addHierarchyNamesToProductsData(resp.data, mapping)
      })
  }

  fetchHierarchyFromAPI(forRefresh?: boolean): CancellableRequest<any> | Promise<any> {
    const {
      nav: {
        queryParams: {
          buyingSessionGroupId,
          orderId,
          orderType,
          clientOrderId,
          clientType
        }
      }
    } = stores

    if (orderId && buyingSessionGroupId) {
      if (this.isEnhancedViewEnabled) {
        return this.fetchHierarchyFromNewAPI(
          {
            buyingSessionGroupId,
            orderId,
            orderType,
            clientOrderId,
            clientType
          },
          forRefresh
        )
      }
      return this.fetchHierarchyFromOldAPI({
        buyingSessionGroupId,
        orderId,
        orderType,
        clientOrderId
      })
    } else {
      return undefined
    }
  }

  fetchHierarchyFromOldAPI = ({
    buyingSessionGroupId,
    orderId,
    orderType,
    clientOrderId
  }) => {
    return this.service.fetchHierarchy(
      buyingSessionGroupId,
      orderId,
      orderType,
      this.mapFiltersToIds(this.filterStore.appliedFilters),
      this.searchKey,
      clientOrderId
    )
  }

  /* This function fetches hierarchy data for new enhanced view API */
  fetchHierarchyFromNewAPI = (
    { buyingSessionGroupId, orderId, orderType, clientOrderId, clientType },
    forRefresh?: boolean
  ) => {
    const {
      productConfiguration: { defaultValuesFromConfig, getActionAttributes }
    } = stores
    const addedGroups = this.cnfGroupByUIStore.addedGroupByAttributes
    const channelName = getChannelName(clientType)

    const searchableAttributes = getActionAttributes(
      'searchBy',
      channelName,
      this.currentViewName,
      true
    )
    const appliedSort = this.enhancedSortStore.selectedSortOption
    let sortData = appliedSort?.id ? appliedSort?.id : ''
    if (sortData) {
      sortData = appliedSort.orderBy === SORT_ORDERS.ASC ? '+' + sortData : '-' + sortData
    }

    const body = {
      heirarchy: {
        sort: parseAppliedSort(this.enhancedSortStore.selectedSortOption),
        filters: {
          ...mapNewFiltersToIds(this.cnfProductFilterStore.selected)
        },
        excludeFilters: {
          ...mapNewFiltersToIds(this.cnfProductFilterStore.excludedFilters || {})
        },
        ...(isEmptyValue(this.searchKey)
          ? {}
          : {
              search: this.searchKey,
              searchAttributes: this.searchKey ? searchableAttributes : []
            }),
        groupBy: addedGroups
          .filter(({ id }) => id)
          .map(({ id, sortId }) => ({ attributeId: id, orderBy: sortId })),
        metricAttributes: METRIC_ATTRIBUTES
      },
      orderTopbarKPIs: {
        metricAttributes: KPI_TOPBAR_METRIC_ATTRIBUTES,
        groupBy: [
          orderType === OrderType.Door
            ? DOOR_ORDER_LEVEL_GROUP_ATTR
            : CLIENT_ORDER_LEVEL_GROUP_ATTR
        ]
      }
    }

    return this.service
      .fetchOrderProductGroups(
        buyingSessionGroupId,
        orderId,
        orderType,
        clientOrderId,
        body
      )
      .then(resp => {
        const tempData = cloneDeep(resp?.data?.heirarchy)
        removeSkippedGroups(tempData)
        formatProductGroupsData(tempData, defaultValuesFromConfig?.empty)
        const topbarData = {
          ...resp?.data?.orderTopbarKPIs,
          totalProducts:
            resp?.data?.orderTopbarKPIs.totalProducts ??
            resp?.data?.orderTopbarKPIs.countOfClientOrderProductId
        }
        orderKPIGlobalTopBarStore.setEnhancedOrderKPIsForGlobalTopBar(topbarData)
        if (forRefresh) {
          this.heirarchy = tempData?.children ?? []
        }
        return tempData?.children ?? []
      })
      .catch(e => {
        throw new Error('bb.settings.productCardConfigSettings.fetchHierarchy')
      })
  }

  @action
  fetchAndPrepareFilterAttributesData = viewName => {
    const {
      nav: {
        queryParams: {
          buyingSessionGroupId,
          clientType,
          clientOrderId,
          orderId,
          orderType
        }
      },
      productConfiguration: { getActionAttributes }
    } = stores
    if (!orderId) {
      return undefined
    }
    const filterAttributes = getActionAttributes(
      'filters',
      getChannelName(clientType),
      viewName
    )
    const aggregateAttributes = map(filterAttributes, 'id')
    const requestBody = {
      aggregateAttributes: aggregateAttributes
    }
    const orderPath =
      orderType === OrderType.Client
        ? `/clientOrder/${orderId}/aggregate-attributes`
        : `/clientOrder/${clientOrderId}/doorOrder/${orderId}/aggregate-attributes`
    const url = `${config.buyingBoardServiceRestEndpoint}${PRODUCT_SERVICE_ENDPOINTS.ENHANCED_PRODUCT_COMMON_PATH}/${buyingSessionGroupId}${orderPath}`

    if (aggregateAttributes.length) {
      this.cnfProductFilterStore.fetchFilterAttributeValues(
        url,
        requestBody,
        filterAttributes,
        aggregateAttributes
      )
    }
  }

  fetchFilterAttributesFromAPI(): CancellableRequest<any> | Promise<any> {
    const {
      nav: {
        queryParams: { buyingSessionGroupId, orderId, orderType }
      }
    } = stores

    // Do not call old filters API for an enhanced view
    if (this.isEnhancedViewEnabled) {
      return undefined
    }
    return buyingSessionGroupId && orderId
      ? this.service.fetchFilterAttributes(buyingSessionGroupId, orderId, orderType)
      : undefined
  }

  @action
  getSearchTextParam = () => {
    return isEmptyValue(this.searchKey)
      ? {}
      : {
          search: this.searchKey
        }
  }

  removeItemAPI(productId: string) {
    const {
      nav: {
        queryParams: { orderId, orderType, buyingSessionGroupId, clientOrderId }
      }
    } = stores
    if (this.isEnhancedViewEnabled) {
      const orderAggregation =
        orderType === OrderType.Door
          ? { ...DOOR_KPI_ORDER_LEVEL_AGGR }
          : { ...CLIENT_KPI_ORDER_LEVEL_AGGR }

      const body = {
        filters: {
          ...mapNewFiltersToIds(this.cnfProductFilterStore.selected)
        },
        excludeFilters: {
          ...mapNewFiltersToIds(this.cnfProductFilterStore.excludedFilters || {})
        },
        ...this.getSearchTextParam(),
        ...orderAggregation
      }
      this.service
        .removeProductFromEnhancedOrder(
          productId,
          orderId,
          buyingSessionGroupId,
          clientOrderId,
          body,
          orderType
        )
        .then(respData => {
          let temp = respData?.orderTopbarKPIs
          temp = {
            ...temp,
            totalProducts: temp.totalProducts ?? temp.countOfClientOrderProductId
          }
          orderKPIGlobalTopBarStore.setEnhancedOrderKPIsForGlobalTopBar(temp)
        })
      Promise.resolve(undefined)
    } else {
      return this.service.removeProductToOrder(orderId, productId, orderType as OrderType)
    }
  }

  @action
  removeProductFromOrderDetailsPopup = (productId: string) => {
    const {
      nav: {
        queryParams: { orderId, orderType, buyingSessionGroupId, clientOrderId }
      }
    } = stores
    const orderAggregation =
      orderType === OrderType.Door
        ? { ...DOOR_KPI_ORDER_LEVEL_AGGR }
        : { ...CLIENT_KPI_ORDER_LEVEL_AGGR }

    const body = {
      filters: {
        ...mapNewFiltersToIds(this.cnfProductFilterStore.selected)
      },
      excludeFilters: {
        ...mapNewFiltersToIds(this.cnfProductFilterStore.excludedFilters || {})
      },
      ...this.getSearchTextParam(),
      ...orderAggregation
    }

    return this.service
      .removeProductFromEnhancedOrder(
        productId,
        orderId,
        buyingSessionGroupId,
        clientOrderId,
        body,
        orderType
      )
      .then(respData => {
        let temp = respData?.orderTopbarKPIs
        temp = {
          ...temp,
          totalProducts: temp.totalProducts ?? temp.countOfClientOrderProductId
        }
        orderKPIGlobalTopBarStore.setEnhancedOrderKPIsForGlobalTopBar(temp)
        return respData
      })
  }

  @action
  addProuductToOrderAgain = (productId, summary) => {
    const {
      queryParams: { orderType, buyingSessionGroupId, orderId, clientType }
    } = nav
    const clientOrderId = summary?.clientOrder?.clientOrderId ?? summary?.clientOrder?.id
    const doorOrderIds =
      clientType === getEnumValue(VIPClientType.Internal)
        ? []
        : map(
            filter(summary.doorOrders, doorOrder => !doorOrder?.deleted),
            'id'
          )
    const orderAggregation =
      orderType === OrderType.Door
        ? { ...DOOR_KPI_ORDER_LEVEL_AGGR }
        : { ...CLIENT_KPI_ORDER_LEVEL_AGGR, doorOrderIds }

    const body = {
      filters: {
        ...mapNewFiltersToIds(this.cnfProductFilterStore.selected)
      },
      excludeFilters: {
        ...mapNewFiltersToIds(this.cnfProductFilterStore.excludedFilters || {})
      },
      ...this.getSearchTextParam(),
      ...orderAggregation
    }
    return this.service
      .addProductToEnhancedOrder(
        productId,
        orderType !== OrderType.Door ? clientOrderId : doorOrderIds[0] ?? orderId,
        buyingSessionGroupId,
        orderType !== OrderType.Door ? '' : clientOrderId,
        body,
        orderType
      )
      .then(response => {
        const { orderTopbarKPIs } = response
        const topbar = {
          totalProductQuantity: orderTopbarKPIs?.totalProductQuantity,
          totalProductValue: orderTopbarKPIs?.totalProductValue,
          totalDoorQuantity: orderTopbarKPIs?.totalDoorQuantity,
          totalDoorValue: orderTopbarKPIs?.totalDoorValue,
          totalSizeQuantity: orderTopbarKPIs?.totalSizeQuantity,
          totalSizeValue: orderTopbarKPIs?.totalSizeValue,
          totalProducts:
            orderTopbarKPIs?.totalProducts ?? orderTopbarKPIs?.countOfClientOrderProductId
        }
        orderKPIGlobalTopBarStore.setEnhancedOrderKPIsForGlobalTopBar(topbar)
        return response
      })
  }

  @action
  removeHierarchy(heirarchyStr: string[]) {
    super.removeHierarchy(heirarchyStr)
    this.removeHierarchyRecursively(this.hierarchyWithoutFilters, heirarchyStr)
    this.hierarchyWithoutFilters = [...this.hierarchyWithoutFilters]
  }

  @action
  async removeItemObjectFromHierarchy(
    index: number,
    resFromCache?: Record<string, any>,
    isProductRemoved?: Boolean
  ) {
    try {
      const res = await super.removeItemObjectFromHierarchy(
        index,
        resFromCache,
        isProductRemoved
      )
      if (!this.isEnhancedViewEnabled) {
        const { clientOrder, doorOrder } = res
        orderKPIGlobalTopBarStore.updateOrderKPIByOrderTypes({ clientOrder, doorOrder })
      }
    } catch (e) {
      throw e
    } finally {
      this.itemIndexObjectStore.setRangeLoading(
        index,
        index + 1,
        PRODUCT_REMOVE_LOADER_KEY,
        false
      )
    }
  }

  @action
  onProductRemove = (productId, apiResponse) => {
    // based on order type set respective response
    const {
      nav: {
        queryParams: { orderType }
      }
    } = stores
    if (this.removedProductsResCache[productId]) {
      return this.removedProductsResCache[productId]
    }
    const {
      totalByDraftQuantity,
      totalValueByDraftQuantity,
      totalBySizeQuantity,
      totalValueBySizeQuantity
    } =
      (orderType === OrderType.Client
        ? apiResponse?.clientOrder
        : apiResponse?.doorOrder) || {}
    this.setKPIs({
      draft: { totalQty: totalByDraftQuantity, totalValue: totalValueByDraftQuantity },
      sized: { totalQty: totalBySizeQuantity, totalValue: totalValueBySizeQuantity }
    })
    this.updateRemovedProductResponseCache(productId, undefined)
  }

  updateRemovedProductResponseCache = (bspId: string, res: any) => {
    this.removedProductsResCache[bspId] = res
  }

  replaceDataServiceWith(service) {
    this.clearAPIRequestCache(HIERARCHY_API_KEY.HEIRARCHY)
    this.clearAPIRequestCache(HIERARCHY_API_KEY.FILTER_ATTRIBUTES)
    this.clearAPIRequestCache(HIERARCHY_API_KEY.ITEMS)
    this.itemIndexObjectStore.clearAll()
    this.heirarchy = []
    this.service = service
  }

  @action
  async init() {
    await super.init()
    this.overviewReactions.push(
      reaction(
        () => this.heirarchy,
        () => {
          if (!this.filterStore.appliedFilters?.length && !this.searchKey) {
            this.hierarchyWithoutFilters = toJS(this.heirarchy, {
              recurseEverything: true
            })
          }
        }
      )
    )
  }

  @action
  destroy() {
    super.destroy()
    this.overviewReactions.forEach(disposer => disposer())
    this.overviewReactions = []
    this.removedProductsResCache = {}
  }

  @computed get activities() {
    const hierarchy = map(this.hierarchyWithoutFilters, 'name')
    return join(hierarchy, CATEGORY_UI_SEPERATOR)
  }

  // Product Details
  @action
  toggleProductIsOrderStatus = productId => {
    const product = this.itemIndexObjectStore.idObjectMap.get(productId)
    this.itemIndexObjectStore.idObjectMap.set(productId, {
      ...product,
      ...{ isOrderProductExist: !product.isOrderProductExist }
    })
  }
}
